<template>
  <div class="min-h-screen bg-white dark:bg-gray-950 font-sans antialiased" :class="{ 'dark': isDarkMode }">

    <!-- Get Started Modal -->
    <GetStartedModal :is-visible="showGetStartedModal" @close="showGetStartedModal = false" />

    <!-- Login Modal -->
    <LoginModal
      :is-visible="showLoginModal"
      @close="showLoginModal = false"
      @switch-to-signup="showLoginModal = false; showGetStartedModal = true"
    />

    <!-- Lightbox -->
    <Teleport to="body">
      <Transition name="lb">
        <div
          v-if="lightbox.open"
          class="fixed inset-0 z-[200] bg-black/85 flex items-center justify-center p-6"
          @click.self="closeLightbox"
        >
          <button
            @click="closeLightbox"
            class="absolute top-5 right-6 text-white/60 hover:text-white text-2xl leading-none transition-colors"
            aria-label="Close"
          >✕</button>
          <video
            v-if="lightbox.type === 'video'"
            :src="lightbox.src"
            autoplay
            controls
            loop
            playsinline
            class="max-w-full max-h-[90vh] rounded-2xl"
          />
          <img
            v-else
            :src="lightbox.src"
            class="max-w-full max-h-[90vh] rounded-2xl object-contain"
          />
        </div>
      </Transition>
    </Teleport>

    <!-- Header -->
    <header class="sticky top-0 z-50 bg-white/80 dark:bg-gray-950/80 backdrop-blur-sm border-b border-zinc-100 dark:border-zinc-800">
      <div class="px-8 h-14 flex items-center justify-between">
        <router-link to="/" class="flex items-center">
          <img :src="logoImage" alt="cv-tool.com" class="hidden sm:block h-7 w-auto" />
          <img :src="mobileLogoImage" alt="cv-tool.com" class="block sm:hidden h-7 w-auto" />
        </router-link>
        <div class="flex items-center gap-4">
          <div v-if="!isAuthenticated" class="flex items-center gap-4">
            <router-link to="/pricing" class="text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors">
              Pricing
            </router-link>
            <button @click="goToLogin" class="text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors">
              Log in
            </button>
            <button @click="createForFree" class="px-4 py-1.5 rounded-lg text-sm font-medium text-white bg-primary hover:opacity-90 transition-opacity">
              Create for Free
            </button>
          </div>
          <div v-else class="flex items-center gap-3">
            <button @click="createForFree" class="px-4 py-1.5 rounded-lg text-sm font-medium text-white bg-primary hover:opacity-90 transition-opacity">
              Create CV
            </button>
            <UserDropdown :current-user="currentUser" @logout="handleLogout" />
          </div>
        </div>
      </div>
    </header>

    <!-- Hero Section -->
    <section class="pt-16 lg:pt-24 pb-16">
      <div class="px-8 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

        <!-- Left: copy + CTA -->
        <div class="text-center lg:text-left">
          <p class="text-xs font-semibold uppercase tracking-widest text-gold mb-5">The CV builder for finance & consulting</p>
          <h1 class="text-4xl lg:text-5xl font-bold text-zinc-900 dark:text-white leading-tight mb-6">
            Your CV, optimized for
            <span class="text-primary dark:text-primary-400">MBB, IB &amp; PE</span>
          </h1>
          <p class="text-lg text-zinc-500 dark:text-zinc-400 mb-8 leading-relaxed">
            One master profile, a tailored CV for every application — the M&amp;A angle for the bank, the strategy angle for the consultancy. Recruiter-ready in minutes.
          </p>
          <div class="flex flex-col items-center lg:items-start gap-3">
            <button @click="createForFree" class="px-7 py-3 rounded-xl text-sm font-semibold text-white bg-primary hover:opacity-90 transition-opacity">
              Create for Free
            </button>
            <p class="text-xs text-zinc-400 dark:text-zinc-500">No account required</p>
          </div>
        </div>

        <!-- Right: live-tailored CV preview -->
        <div>
          <div class="border border-zinc-100 dark:border-zinc-800 rounded-2xl overflow-hidden">
            <!-- Paper sheet: stays white in dark mode, like a real document -->
            <div class="relative bg-white px-8 py-7 font-serif text-zinc-800">
              <div class="text-center mb-5">
                <p class="text-base font-bold tracking-[0.18em] text-zinc-900">MAXIMILIAN WEBER</p>
                <p class="text-[10px] text-zinc-500 mt-1">+49 (30) 123-4567 &nbsp;|&nbsp; maximilian.weber@fu.edu &nbsp;|&nbsp; Frankfurt am Main, Germany</p>
              </div>

              <p class="text-[11px] font-bold tracking-[0.14em] text-zinc-900 border-b border-zinc-300 pb-1 mb-2.5">WORK EXPERIENCE</p>

              <div class="mb-3">
                <div class="flex justify-between items-baseline">
                  <p class="text-[11px] font-bold text-zinc-900">Summer Analyst &middot; Goldman Sachs</p>
                  <p class="text-[10px] italic text-zinc-500">Jun 2023 – Aug 2023</p>
                </div>
                <ul class="mt-1 space-y-0.5">
                  <li class="text-[10px] text-zinc-600 pl-3 relative before:content-['•'] before:absolute before:left-0.5">Executed M&amp;A transactions totaling €1.8B+ across German DAX companies</li>
                  <li class="text-[10px] text-zinc-600 pl-3 relative before:content-['•'] before:absolute before:left-0.5">Built comprehensive financial models (DCF, LBO, trading comps)</li>
                </ul>
              </div>

              <div>
                <div class="flex justify-between items-baseline">
                  <p class="text-[11px] font-bold text-zinc-900">Investment Banking Intern &middot; Deutsche Bank</p>
                  <p class="text-[10px] italic text-zinc-500">Jul 2022 – Sep 2022</p>
                </div>
                <ul class="mt-1 space-y-0.5">
                  <li class="text-[10px] text-zinc-600 pl-3 relative before:content-['•'] before:absolute before:left-0.5">Supported €500M+ IPO process for a German technology company</li>
                  <li class="text-[10px] text-zinc-600 pl-3 relative before:content-['•'] before:absolute before:left-0.5">Conducted industry research on German Mittelstand companies</li>
                </ul>
              </div>

              <p class="text-[11px] font-bold tracking-[0.14em] text-zinc-900 border-b border-zinc-300 pb-1 mb-2 mt-4">EDUCATION</p>
              <div class="flex justify-between items-baseline">
                <p class="text-[11px] font-bold text-zinc-900">Finance &middot; London School of Economics</p>
                <p class="text-[10px] italic text-zinc-500">Sep 2022 – Jun 2024</p>
              </div>
              <ul class="mt-1 space-y-0.5 min-h-[32px]">
                <li
                  v-for="(b, i) in typedBullets"
                  :key="i"
                  v-show="b.length > 0"
                  class="text-[10px] text-zinc-600 pl-3 relative before:content-['•'] before:absolute before:left-0.5"
                >{{ b }}<span v-if="typingIndex === i" class="type-caret"></span></li>
              </ul>

              <!-- Bottom fade: hints the page continues -->
              <div class="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
            </div>
          </div>
          <p class="text-xs text-zinc-400 dark:text-zinc-500 text-center mt-3">
            Generated from your profile — the exact one-page format top firms expect.
          </p>
        </div>

      </div>
    </section>

    <!-- Logo strip -->
    <section class="pb-20">
      <div class="px-8 max-w-5xl mx-auto">
        <p class="text-center text-xs font-semibold uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mb-8">
          Trusted by candidates placed at the world's most competitive firms
        </p>
        <div class="flex flex-wrap items-center justify-center gap-x-10 gap-y-7">
          <img
            v-for="logo in logoWall"
            :key="logo.alt"
            :src="logo.src"
            :alt="logo.alt"
            :class="[logo.h, 'w-auto object-contain grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition duration-200']"
          />
        </div>
      </div>
    </section>

    <div class="border-t border-zinc-100 dark:border-zinc-800 mx-8"></div>

    <!-- How it works -->
    <section class="py-16">
      <div class="px-8 max-w-5xl mx-auto">
        <div class="text-center mb-12">
          <p class="text-xs font-semibold uppercase tracking-widest text-gold mb-3">How it works</p>
          <h2 class="text-2xl lg:text-3xl font-bold text-zinc-900 dark:text-white">From profile to PDF in four steps.</h2>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8">
          <div>
            <p class="text-2xl font-bold text-gold mb-2">01</p>
            <h3 class="text-base font-semibold text-zinc-900 dark:text-white mb-2">Build your master profile</h3>
            <p class="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">
              Import an existing CV or add your experience, education, and skills once. They stay saved for every future application.
            </p>
          </div>
          <div>
            <p class="text-2xl font-bold text-gold mb-2">02</p>
            <h3 class="text-base font-semibold text-zinc-900 dark:text-white mb-2">Tailor per application</h3>
            <p class="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">
              Select the entries that fit the role leveraging alternative framings of the same position when the angle matters.
            </p>
          </div>
          <div>
            <p class="text-2xl font-bold text-gold mb-2">03</p>
            <h3 class="text-base font-semibold text-zinc-900 dark:text-white mb-2">Let AI sharpen your bullets</h3>
            <p class="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">
              AI rewrites your bullets points into crisp, impact-driven lines, drawing on every entry across your profile.
            </p>
          </div>
          <div>
            <p class="text-2xl font-bold text-gold mb-2">04</p>
            <h3 class="text-base font-semibold text-zinc-900 dark:text-white mb-2">Download &amp; apply</h3>
            <p class="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">
              Get a polished one-page PDF, with your reference documents merged into one consolidated file, ready to send.
            </p>
          </div>
        </div>

        <!-- Workflow video -->
        <div class="mt-14 max-w-4xl mx-auto">
          <div
            class="border border-zinc-100 dark:border-zinc-800 rounded-2xl overflow-hidden hover:border-primary dark:hover:border-primary-400 transition-colors duration-150 cursor-zoom-in"
            @click="openLightbox('video', heroVideo)"
          >
            <video
              :src="heroVideo"
              autoplay
              muted
              loop
              playsinline
              class="w-full block pointer-events-none"
            />
          </div>
          <p class="text-center text-xs text-zinc-400 dark:text-zinc-500 mt-3">The full workflow — from profile to downloaded CV in 20 seconds.</p>
        </div>
      </div>
    </section>

    <div class="border-t border-zinc-100 dark:border-zinc-800 mx-8"></div>

    <!-- Features Section -->
    <section class="py-16 bg-white dark:bg-gray-950">
      <div class="px-8 space-y-20">

        <!-- Row 1: Master profile variants (screenshot left, text right) -->
        <div class="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div class="order-2 lg:order-1">
            <div
              class="border border-zinc-100 dark:border-zinc-800 rounded-2xl overflow-hidden hover:border-primary dark:hover:border-primary-400 transition-colors duration-150 cursor-zoom-in"
              @click="openLightbox('image', profileScreenshot)"
            >
              <!-- Window chrome -->
              <div class="flex items-center gap-1.5 px-4 py-2.5 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900">
                <span class="w-2.5 h-2.5 rounded-full bg-red-300"></span>
                <span class="w-2.5 h-2.5 rounded-full bg-amber-300"></span>
                <span class="w-2.5 h-2.5 rounded-full bg-green-300"></span>
              </div>
              <img :src="profileScreenshot" alt="Master profile with one position saved in three framings" class="w-full block pointer-events-none" />
            </div>
          </div>
          <div class="order-1 lg:order-2 max-w-md">
            <p class="text-xs font-semibold uppercase tracking-widest text-gold mb-3">Master profile</p>
            <h3 class="text-2xl lg:text-3xl font-bold text-zinc-900 dark:text-white mb-4">One position. Many framings.</h3>
            <p class="text-base text-zinc-500 dark:text-zinc-400 leading-relaxed">
              Save alternative versions of the same role — an M&amp;A angle next to a capital-markets one — and pick the right framing when you create a CV. Your profile stays the single source of truth.
            </p>
          </div>
        </div>

        <!-- Row 2: Dashboard (text left, mockup right) -->
        <div class="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div class="max-w-md">
            <p class="text-xs font-semibold uppercase tracking-widest text-gold mb-3">Workflow</p>
            <h3 class="text-2xl lg:text-3xl font-bold text-zinc-900 dark:text-white mb-4">Every application, organized.</h3>
            <p class="text-base text-zinc-500 dark:text-zinc-400 leading-relaxed">
              Your dashboard keeps every CV next to your master profile. Duplicate one for the next application, sync in profile updates, and re-download whenever you need it.
            </p>
          </div>
          <div
            class="border border-zinc-100 dark:border-zinc-800 rounded-2xl overflow-hidden hover:border-primary dark:hover:border-primary-400 transition-colors duration-150 cursor-zoom-in"
            @click="openLightbox('image', dashboardScreenshot)"
          >
            <!-- Window chrome -->
            <div class="flex items-center gap-1.5 px-4 py-2.5 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900">
              <span class="w-2.5 h-2.5 rounded-full bg-red-300"></span>
              <span class="w-2.5 h-2.5 rounded-full bg-amber-300"></span>
              <span class="w-2.5 h-2.5 rounded-full bg-green-300"></span>
            </div>
            
            <img :src="dashboardScreenshot" alt="Dashboard with all CVs next to the master profile" class="w-full block pointer-events-none" />
          </div>
        </div>

                <!-- Row 3: AI-optimized bullets (screenshot left, text right) -->
        <div class="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div class="order-2 lg:order-1">
            <div
              class="border border-zinc-100 dark:border-zinc-800 rounded-2xl overflow-hidden hover:border-primary dark:hover:border-primary-400 transition-colors duration-150 cursor-zoom-in"
              @click="openLightbox('image', aiScreenshot)"
            >
              <!-- Window chrome -->
              <div class="flex items-center gap-1.5 px-4 py-2.5 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900">
                <span class="w-2.5 h-2.5 rounded-full bg-red-300"></span>
                <span class="w-2.5 h-2.5 rounded-full bg-amber-300"></span>
                <span class="w-2.5 h-2.5 rounded-full bg-green-300"></span>
              </div>
              <img :src="aiScreenshot" alt="Master profile with one position saved in three framings" class="w-full block pointer-events-none" />
            </div>
          </div>
          <div class="order-1 lg:order-2 max-w-md">
            <p class="text-xs font-semibold uppercase tracking-widest text-gold mb-3">AI Optimization</p>
            <h3 class="text-2xl lg:text-3xl font-bold text-zinc-900 dark:text-white mb-4">The right story for every role.</h3>
            <p class="text-base text-zinc-500 dark:text-zinc-400 leading-relaxed">
              A great CV isn't just about what you've done — it's about what matters. AI reviews the job description, your profile, and reference materials to recommend targeted bullet points that align your experience with the position.
            </p>
          </div>
        </div>

        <!-- Mid-page CTA -->
        <div class="max-w-6xl mx-auto text-center pt-2">
          <button @click="createForFree" class="px-7 py-3 rounded-xl text-sm font-semibold text-white bg-primary hover:opacity-90 transition-opacity">
            Create your first CV
          </button>
          <p class="text-xs text-zinc-400 dark:text-zinc-500 mt-3">No signup required — start now, save later</p>
        </div>

      </div>
    </section>

    <!-- Testimonials Section -->
    <section class="py-16 border-t border-zinc-100 dark:border-zinc-800">
      <div class="px-8 max-w-6xl mx-auto">
        <div class="text-center mb-14">
          <p class="text-xs font-semibold uppercase tracking-widest text-gold mb-3">What candidates say</p>
          <h2 class="text-2xl lg:text-3xl font-bold text-zinc-900 dark:text-white">Used by candidates at the world's top firms.</h2>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">

          <!-- Testimonial 1 -->
          <div class="border border-zinc-100 dark:border-zinc-800 rounded-2xl p-7 flex flex-col gap-5 hover:border-primary dark:hover:border-primary-400 transition-colors duration-150">
            <span class="text-3xl font-serif text-gold leading-none">"</span>
            <p class="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed flex-1">
              I was applying to 10 firms simultaneously and needed a tailored CV for each. With cv-tool, I could spin up a new version in under five minutes. The format is exactly what MBB recruiters expect — I stopped worrying about layout entirely.
            </p>
            <div class="border-t border-zinc-100 dark:border-zinc-800 pt-4">
              <p class="text-sm font-semibold text-zinc-900 dark:text-white">Julia K.</p>
              <p class="text-xs text-gold mt-0.5">Business Analyst · McKinsey & Company</p>
            </div>
          </div>

          <!-- Testimonial 2 -->
          <div class="border border-zinc-100 dark:border-zinc-800 rounded-2xl p-7 flex flex-col gap-5 hover:border-primary dark:hover:border-primary-400 transition-colors duration-150">
            <span class="text-3xl font-serif text-gold leading-none">"</span>
            <p class="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed flex-1">
              I used to spend hours fighting Word to get my CV to look right. Margins, fonts, bullet alignment — it was a nightmare. cv-tool produces a flawless result every single time. The quality of the output genuinely surprised me.
            </p>
            <div class="border-t border-zinc-100 dark:border-zinc-800 pt-4">
              <p class="text-sm font-semibold text-zinc-900 dark:text-white">Maximilian S.</p>
              <p class="text-xs text-gold mt-0.5">Investment Banking Analyst · Goldman Sachs</p>
            </div>
          </div>

          <!-- Testimonial 3 -->
          <div class="border border-zinc-100 dark:border-zinc-800 rounded-2xl p-7 flex flex-col gap-5 hover:border-primary dark:hover:border-primary-400 transition-colors duration-150">
            <span class="text-3xl font-serif text-gold leading-none">"</span>
            <p class="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed flex-1">
              Coming from a non-target university, I needed every part of my application to be perfect. cv-tool gave me the exact one-page format I'd seen on successful candidate CVs. I got first-round interviews at every firm I applied to.
            </p>
            <div class="border-t border-zinc-100 dark:border-zinc-800 pt-4">
              <p class="text-sm font-semibold text-zinc-900 dark:text-white">Sophie L.</p>
              <p class="text-xs text-gold mt-0.5">Private Equity Analyst · CVC Capital Partners</p>
            </div>
          </div>

        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-16 border-t border-zinc-100 dark:border-zinc-800">
      <div class="px-8 text-center max-w-xl mx-auto">
        <p class="text-xs font-semibold uppercase tracking-widest text-gold mb-4">Get started</p>
        <h2 class="text-3xl font-bold text-zinc-900 dark:text-white mb-4">Create your CV in minutes.</h2>
        <p class="text-zinc-500 dark:text-zinc-400 mb-8">No account required. Free forever.</p>
        <button @click="createForFree" class="px-8 py-3 rounded-xl text-sm font-semibold text-white bg-primary hover:opacity-90 transition-opacity">
          Create for Free
        </button>
      </div>
    </section>

    <!-- Footer -->
    <footer class="bg-primary py-8">
      <div class="px-8 flex items-center justify-between">
        <img :src="logoImage" alt="cv-tool.com" class="h-6 w-auto brightness-0 invert opacity-70" />
        <div class="flex items-center gap-6">
          <router-link to="/pricing" class="text-xs text-white/50 hover:text-white/80 transition-colors">Pricing</router-link>
          <p class="text-xs text-white/40">© 2025 cv-tool.com</p>
        </div>
      </div>
    </footer>

  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { useAuth } from './composables/useAuth.js'
import logoImage from './assets/icons/cv-tool-gold.png'
import mobileLogoImage from './assets/icons/mobile-logo.png'
import heroVideo from './assets/images/hero-video.mov'
import profileScreenshot from './assets/images/profile-screenshot.png'
import dashboardScreenshot from './assets/images/dashboard-screenshot.png'
import aiScreenshot from './assets/images/ai-screenshot.png'

import logoMcKinsey from './assets/landing-page-logos/mckinsey.png'
import logoBcg from './assets/landing-page-logos/bcg.png'
import logoBain from './assets/landing-page-logos/bain.png'
import logoGoldmanSachs from './assets/landing-page-logos/goldman-sachs.png'
import logoMorganStanley from './assets/landing-page-logos/morgan-stanley.png'
import logoDeutscheBank from './assets/landing-page-logos/deutsche-bank.png'
import logoUbs from './assets/landing-page-logos/ubs.png'
import logoHsbc from './assets/landing-page-logos/hsbc.png'
import logoOxford from './assets/landing-page-logos/oxford.png'
import logoCambridge from './assets/landing-page-logos/cambridge.png'
import logoLse from './assets/landing-page-logos/lse.png'
import logoBerkeley from './assets/landing-page-logos/uc-berkeley.png'
import logoMannheim from './assets/landing-page-logos/uni-mannheim.png'
import logoTum from './assets/landing-page-logos/tum.png'
import logoCdtm from './assets/landing-page-logos/cdtm.png'
import logoHsg from './assets/landing-page-logos/hsg.png'
import UserDropdown from './components/UserDropdown.vue'
import GetStartedModal from './components/GetStartedModal.vue'
import LoginModal from './components/LoginModal.vue'

export default {
  name: 'LandingPage',
  components: {
    UserDropdown,
    GetStartedModal,
    LoginModal
  },
  setup() {
    const isDarkMode = ref(false)
    const isAuthenticated = ref(false)
    const currentUser = ref(null)
    let mediaQuery = null

    const showGetStartedModal = ref(false)
    const showLoginModal = ref(false)

    const lightbox = ref({ open: false, type: null, src: null })
    const openLightbox = (type, src) => { lightbox.value = { open: true, type, src } }
    const closeLightbox = () => { lightbox.value = { open: false, type: null, src: null } }

    // Firms first, then universities. `h` caps each logo's height so the
    // mixed-size source files sit on a visually even line in the strip.
    const logoWall = [
      { src: logoMcKinsey, alt: 'McKinsey & Company', h: 'max-h-7' },
      { src: logoBcg, alt: 'Boston Consulting Group', h: 'max-h-6' },
      { src: logoBain, alt: 'Bain & Company', h: 'max-h-9' },
      { src: logoGoldmanSachs, alt: 'Goldman Sachs', h: 'max-h-7' },
      { src: logoMorganStanley, alt: 'Morgan Stanley', h: 'max-h-5' },
      { src: logoDeutscheBank, alt: 'Deutsche Bank', h: 'max-h-8' },
      { src: logoUbs, alt: 'UBS', h: 'max-h-6' },
      { src: logoHsbc, alt: 'HSBC', h: 'max-h-9' },
      { src: logoOxford, alt: 'University of Oxford', h: 'max-h-9' },
      { src: logoCambridge, alt: 'University of Cambridge', h: 'max-h-9' },
      { src: logoLse, alt: 'London School of Economics', h: 'max-h-8' },
      { src: logoBerkeley, alt: 'UC Berkeley', h: 'max-h-9' },
      { src: logoMannheim, alt: 'Universität Mannheim', h: 'max-h-9' },
      { src: logoTum, alt: 'Technical University of Munich', h: 'max-h-6' },
      { src: logoCdtm, alt: 'CDTM', h: 'max-h-8' },
      { src: logoHsg, alt: 'University of St. Gallen', h: 'max-h-8' }
    ]

    // Hero CV preview: the LSE education bullets type themselves out, cycling
    // through framings tailored to different application targets.
    const tailorTargets = [
      {
        label: 'M&A focus',
        bullets: [
          "Graduated with Distinction (top 10% of class), Dean's List",
          'Relevant coursework: Corporate Finance, Private Equity, M&A Strategy'
        ]
      },
      {
        label: 'Markets focus',
        bullets: [
          "Master's thesis on ETF liquidity in stressed markets — Distinction",
          'Relevant coursework: Asset Pricing, Fixed Income & Derivatives'
        ]
      },
      {
        label: 'Strategy focus',
        bullets: [
          'President of LSE Consulting Society — led 12 student engagements',
          'Relevant coursework: Strategy, Managerial Economics, Game Theory'
        ]
      }
    ]
    const activeTarget = ref(0)
    const typedBullets = ref([])
    const typingIndex = ref(-1)
    let typeTimer = null

    const startTypeLoop = () => {
      const bullets = tailorTargets[activeTarget.value].bullets
      typedBullets.value = bullets.map(() => '')
      let bi = 0
      let pos = 0
      const typeChar = () => {
        if (bi >= bullets.length) {
          typingIndex.value = -1
          typeTimer = setTimeout(() => {
            activeTarget.value = (activeTarget.value + 1) % tailorTargets.length
            startTypeLoop()
          }, 3200)
          return
        }
        typingIndex.value = bi
        pos++
        typedBullets.value[bi] = bullets[bi].slice(0, pos)
        if (pos >= bullets[bi].length) {
          bi++
          pos = 0
        }
        typeTimer = setTimeout(typeChar, 14)
      }
      typeChar()
    }

    const checkAuthStatus = async () => {
      try {
        const response = await fetch('/api/users/current', { headers: { 'Accept': 'application/json' } })
        if (response.ok) {
          const data = await response.json()
          if (data.success) {
            isAuthenticated.value = true
            currentUser.value = data.user
          } else {
            isAuthenticated.value = false
            currentUser.value = null
          }
        } else {
          isAuthenticated.value = false
          currentUser.value = null
        }
      } catch {
        isAuthenticated.value = false
        currentUser.value = null
      }
    }

    // Shared logout: resets the cached auth state used by the router guard
    // and reloads to the landing page.
    const { handleLogout } = useAuth()

    const updateDarkMode = (e) => { isDarkMode.value = e.matches }

    onMounted(() => {
      mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      isDarkMode.value = mediaQuery.matches
      mediaQuery.addEventListener('change', updateDarkMode)
      checkAuthStatus()
      startTypeLoop()
    })

    onUnmounted(() => {
      if (mediaQuery) mediaQuery.removeEventListener('change', updateDarkMode)
      clearTimeout(typeTimer)
    })

    const createForFree = () => { showGetStartedModal.value = true }
    const signUpNow = () => { showGetStartedModal.value = true }
    const goToLogin = () => { showLoginModal.value = true }

    return {
      isDarkMode,
      isAuthenticated,
      currentUser,
      createForFree,
      signUpNow,
      goToLogin,
      handleLogout,
      showGetStartedModal,
      showLoginModal,
      lightbox,
      openLightbox,
      closeLightbox,
      logoWall,
      typedBullets,
      typingIndex,
      logoImage,
      mobileLogoImage,
      heroVideo,
      profileScreenshot,
      dashboardScreenshot,
      aiScreenshot
    }
  }
}
</script>

<style scoped>
.lb-enter-active, .lb-leave-active { transition: opacity 0.15s ease; }
.lb-enter-from, .lb-leave-to { opacity: 0; }

.type-caret {
  display: inline-block;
  width: 1.5px;
  height: 0.95em;
  margin-left: 2px;
  vertical-align: -0.1em;
  background: currentColor;
  animation: caret-blink 1.1s step-end infinite;
}
@keyframes caret-blink { 50% { opacity: 0; } }
</style>
